import {Editor, PerformSingleInputTableOpFunc} from 'notebook/mediocre/EditorTypes';
import {NoteEditorProps} from 'notebook/notes/NoteEditorTypes';
import {TableEditableProps} from 'notebook/tables/core-ui/TableEditableProps';

interface TableEditablePropsFromEditor {
  fileId: TableEditableProps['fileId'];
  locked: TableEditableProps['locked'];
  dirty: TableEditableProps['dirty'];
  // restore state from history on undo
  restoreState: TableEditableProps['restoreState'];
  // store the state of an entry in edit history
  storeState: TableEditableProps['storeState'];
  requestSelection: TableEditableProps['requestSelection'];
  disableMaximizedTables?: TableEditableProps['disableMaximizedTables'];
  viewer: TableEditableProps['viewer'];
  renameTable: TableEditableProps['renameTable'];
  isTableNameUsed: TableEditableProps['isTableNameUsed'];
  lookUpTableNameWithActiveEdit: TableEditableProps['lookUpTableNameWithActiveEdit'];
  tryInsert: TableEditableProps['tryInsert'];
  canInsert: TableEditableProps['canInsert'];
  setMultiEditingForTable: TableEditableProps['setMultiEditingForTable'];
  deleteRows: TableEditableProps['deleteRows'];
  deleteRowsNonContiguous: TableEditableProps['deleteRowsNonContiguous'];
  deleteColumns: TableEditableProps['deleteColumns'];
  insertRowsAbove: TableEditableProps['insertRowsAbove'];
  insertRowsBelow: TableEditableProps['insertRowsBelow'];
  reorderRowsOrColumns: TableEditableProps['reorderRowsOrColumns'];
  insertColumnsLeft: TableEditableProps['insertColumnsLeft'];
  insertColumnsRight: TableEditableProps['insertColumnsRight'];
  performTableOp: TableEditableProps['performTableOp'];
  isParentLockedWithEditWarn: TableEditableProps['isParentLockedWithEditWarn'];
  insertItemHandler: TableEditableProps['insertItemHandler'];
  deleteHandler: TableEditableProps['deleteHandler'];
  tabHandler: TableEditableProps['tabHandler'];
  moveHandler: TableEditableProps['moveHandler'];
  setClipboard: TableEditableProps['setClipboard'];
  setMaximizedTable: TableEditableProps['setMaximizedTable'];
  setSplitScreenTable: TableEditableProps['setSplitScreenTable'];
  setSelectionAbove: TableEditableProps['setSelectionAbove'];
  setSelectionBelow: TableEditableProps['setSelectionBelow'];
  canRename: TableEditableProps['canRename'];
  canAddRemoveColumns: TableEditableProps['canAddRemoveColumns'];
  mergeRegion: TableEditableProps['mergeRegion'];
  unmergeRegion: TableEditableProps['unmergeRegion'];
  mergeIntoSingleInput: PerformSingleInputTableOpFunc;
  unmergeSingleInput: PerformSingleInputTableOpFunc;
}

/**
 * NoteEditor implements a certain set of methods / props to support rendering a TableEditable that are
 * threaded into the TableEditable component. If TableEditable is not a direct child of NoteEditor, this
 * becomes particularly annoying to thread through.
 *
 * Instead we thread through the NoteEditor itself, as well as the isolated, reusable props that was intended
 * to be threadable to any component. See MediocreItem.js for motivation.
 *
 * @param  {NoteEditor ReactInstance}
 * @param  {Object} editorProps - See NoteEditor.makeEditorProps
 * @return {Object} - props to be threaded into TableEditable
 */
export function tableEditablePropsFromEditor(
  editor: Editor,
  editorProps: NoteEditorProps
): TableEditablePropsFromEditor {
  return {
    // TableEditable edits
    canRename: !editor.isProtectedAgainstEdit(),
    canAddRemoveColumns: !editor.isProtectedAgainstEdit(),
    deleteColumns: editorProps.deleteTableColumns,
    disableMaximizedTables: editorProps.disableMaximizedTables,
    insertColumnsLeft: editorProps.insertTableColumnsLeft,
    insertColumnsRight: editorProps.insertTableColumnsRight,
    renameTable: editorProps.renameTable,
    performTableOp: editorProps.performTableOp,
    tryInsert: editorProps.tryInsert,
    canInsert: editorProps.canInsert,
    setMultiEditingForTable: editorProps.setMultiEditingForTable,
    isTableNameUsed: editorProps.isTableNameUsed,
    lookUpTableNameWithActiveEdit: editorProps.lookUpTableNameWithActiveEdit,
    deleteRows: editorProps.deleteTableRows,
    deleteRowsNonContiguous: editorProps.deleteTableRowsNonContiguous,
    insertRowsAbove: editorProps.insertTableRowsAbove, // disabled since table has locked dims
    insertRowsBelow: editorProps.insertTableRowsBelow, // disabled since table has locked dims
    reorderRowsOrColumns: editorProps.reorderTableRowsOrColumns,
    mergeIntoSingleInput: editorProps.mergeIntoSingleInput,
    unmergeSingleInput: editorProps.unmergeSingleInput,
    restoreState: editorProps.restoreState,
    storeState: editorProps.storeState,
    requestSelection: editorProps.requestSelection,
    fileId: editorProps.fileId,
    viewer: editorProps.viewer,
    locked: editorProps.locked,
    dirty: editorProps.dirty,
    isParentLockedWithEditWarn: editor.isLockedWithEditWarn,
    mergeRegion: editor.mergeTableRegion,
    unmergeRegion: editor.unmergeTableRegion,

    // Generic item edits
    deleteHandler: editor.itemDeleteHandler,
    tabHandler: editor.tabHandler,
    insertItemHandler: editor.insertItemHandler,
    moveHandler: editor.moveHandler,
    setClipboard: editor.copyItemWithoutText,
    setMaximizedTable: editor.setMaximizedTable,
    setSplitScreenTable: editor.setSplitScreenTable,
    setSelectionAbove: editor.setSelectionAbove,
    setSelectionBelow: editor.setSelectionBelow,
  };
}
